import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, TriangleAlert } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { Card, CardContent } from "./ui/card.js";
import { AsyncButton } from "./ui/async-button.js";

export interface MyShiftStatus {
  hasShift: boolean;
  shiftStartHHMM: string | null;
  shiftEndHHMM: string | null;
  /** ISO timestamp of the one ask they have already sent, or null. */
  nudgeSentAt: string | null;
  /** The note that will be sent, written for them. */
  defaultNote: string;
}

/**
 * "Nobody has set your shift" — shown on the dashboard and on the attendance page.
 *
 * One component rather than two copies, because the two would drift: this is the
 * same fact and the same single action in both places. It renders nothing at all
 * once a shift exists, so neither page has to decide whether to show it.
 *
 * The ask can only be sent once. That is enforced on the server (employees
 * .shiftNudgeSentAt) rather than by disabling the button in local state, which a
 * refresh would undo -- and the manager would then be nudged again on every visit.
 */
export function ShiftNotSetBanner({ onChanged }: { onChanged?: () => void }) {
  const { show } = useToast();
  const [status, setStatus] = useState<MyShiftStatus | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<MyShiftStatus>("/me/shift-status");
      setStatus(data);
    } catch (e) {
      // A banner that cannot load its own state should stay out of the way.
      logError("ShiftNotSetBanner.load", e);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function send() {
    try {
      await api.post("/me/shift-nudge", {});
      show("Your manager has been asked to set your shift", "success");
      await load();
      onChanged?.();
    } catch (e) {
      logError("ShiftNotSetBanner.send", e);
      show(errMsg(e) ?? "Could not send that", "error");
      throw e;
    }
  }

  if (!status || status.hasShift) return null;

  const alreadyAsked = !!status.nudgeSentAt;
  const askedOn = alreadyAsked
    ? new Date(status.nudgeSentAt!).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;

  return (
    <Card className="border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]">
      <CardContent className="flex flex-wrap items-start gap-3 p-4">
        <TriangleAlert size={18} className="mt-0.5 shrink-0 text-[var(--color-warning-fg)]" />
        <div className="min-w-60 flex-1">
          <p className="m-0 text-[14px] font-bold">Your shift timing has not been set</p>
          <p className="m-0 mt-1 text-[13px] text-surface-600">
            <strong>You cannot check in until it is set.</strong> Your manager, senior manager or HR can set it.
          </p>
          <p className="m-0 mt-2 text-[12px] italic text-surface-500">&ldquo;{status.defaultNote}&rdquo;</p>
        </div>

        {alreadyAsked ? (
          // Deliberately not a disabled button: there is nothing to press, and saying
          // when it was sent answers the question the button would have raised.
          <span className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-[var(--color-success-bg)] px-3 py-2 text-[13px] font-semibold text-[var(--color-success-fg)]">
            <CheckCircle2 size={15} />
            Request sent{askedOn ? ` on ${askedOn}` : ""}
          </span>
        ) : (
          <AsyncButton size="sm" variant="warning" onClick={() => send()}>
            Ask my manager to set it
          </AsyncButton>
        )}
      </CardContent>
    </Card>
  );
}

export default ShiftNotSetBanner;
